class CollectionGrid extends HTMLElement {
  connectedCallback() {
    this.#init();
  }

  #init() {
    this.itemsEl = this.querySelector('[data-collection-grid-items]');
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.loadingEl = this.querySelector('[data-infinite-scroll-loading]');
    this.sortSelect = this.querySelector('[data-sort-select]');
    this.sectionId = this.dataset.sectionId;
    this.isLoading = false;

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.#onSortChange());
    }

    if (this.observer) this.observer.disconnect();

    if (this.sentinel) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) this.#loadNextPage();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.sentinel);
    }
  }

  // Called by collection-filters.js after an AJAX filter update swaps the
  // grid/sentinel markup — re-reads the fresh DOM and re-arms the
  // infinite-scroll observer (the old sentinel node is gone by then).
  refresh() {
    this.#init();
  }

  #onSortChange() {
    const url = new URL(window.location.href);
    if (this.sortSelect.value) {
      url.searchParams.set('sort_by', this.sortSelect.value);
    } else {
      url.searchParams.delete('sort_by');
    }
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  async #loadNextPage() {
    if (this.isLoading) return;

    const nextUrl = this.sentinel.dataset.nextUrl;
    if (!nextUrl) {
      this.observer.disconnect();
      return;
    }

    this.isLoading = true;
    if (this.loadingEl) this.loadingEl.hidden = false;

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newItems = doc.querySelector('[data-collection-grid-items]');
      const newSentinel = doc.querySelector('[data-infinite-scroll-sentinel]');

      if (newItems) {
        // Move each product card over instead of replacing innerHTML, so
        // we don't disturb any state on the cards already in view.
        Array.from(newItems.children).forEach((card) => {
          this.itemsEl.appendChild(card);
        });
      }

      // Update (or clear) the "next page" pointer for the next scroll.
      if (newSentinel && newSentinel.dataset.nextUrl) {
        this.sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
      } else {
        this.sentinel.dataset.nextUrl = '';
        this.observer.disconnect();
      }
    } catch (error) {
      console.error('[collection-grid] failed to load next page', error);
    } finally {
      this.isLoading = false;
      if (this.loadingEl) this.loadingEl.hidden = true;
    }
  }
}

if (!customElements.get('collection-grid')) {
  customElements.define('collection-grid', CollectionGrid);
}
